// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, customElement, Disposable } from "aurelia-framework";
import { Router } from "aurelia-router";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { UserWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/UserWorkflowModuleCatalog";
import { Splice } from "datatypes/Splice";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { IWorkflowModuleConfig } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { ICreditsService } from "services/Credits/ICreditsService";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { ConnectedHardwareService, HardwareStatusInfo } from "services/Hardware/ConnectedHardwareService";
import { INotification, INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

@autoinject
@customElement("navbar")
export class NavBar {
    private notificationIconElement: HTMLElement;

    private unreadNotifications = 0;
    private isNotificationPreview = true;
    private newNotifications: INotification[] = [];

    private disposables: Disposable[];

    @computedFrom("clientService.status")
    public get clientUpdateAvailable(): boolean {
        return (
            this.clientService.status === ClientStatus.OutdatedVersion ||
            this.clientService.status === ClientStatus.UnsupportedVersion
        );
    }

    protected get routeSettings(): unknown {
        return this.router.currentInstruction?.config.settings;
    }

    protected get isIndustrialMode(): boolean {
        return this.workflowModeService.selectedWorkflowMode?.isIndustrial ?? false;
    }

    private get canShowHardwareSelection(): boolean {
        // If no workflow mode is selected, always show the hardware selection
        if (!this.workflowModeService.selectedWorkflowMode) return true;

        // If disableForWorkflowModes is not set or empty, show the hardware selection
        const disableList = MeasurementWorkflowModuleCatalog.hardwareSelectionModule.disableForWorkflowModes;
        if (!disableList || disableList.length === 0) return true;

        // If the selected workflow mode is in the disable list, do not show the hardware selection
        return !disableList.includes(this.workflowModeService.selectedWorkflowMode);
    }

    constructor(
        private readonly shellViewManager: ShellViewManager,
        private readonly router: Router,
        private readonly creditsService: ICreditsService,
        private readonly authentication: IAuthentication,
        private readonly clientService: IClientService,
        private readonly serverStatusService: IServerStatusService,
        private readonly notificationService: INotificationService,
        private readonly bindingEngine: BindingEngine,
        private readonly connectedHardwareService: ConnectedHardwareService,
        private readonly camIV64Service: CamIV64Service,
        private readonly workflowModeService: WorkflowModeService
    ) {}

    public bind() {
        this.disposables = [
            this.bindingEngine
                .collectionObserver(this.notificationService.notifications)
                .subscribe(this.notificationsChanged),
        ];

        this.notificationsChanged([
            { addedCount: this.notificationService.notifications.length, index: 0, removed: [] },
        ]);
    }

    public unbind() {
        while (this.disposables.length > 0) this.disposables.pop().dispose();
    }

    public hardwareStatusToIcon(status: HardwareStatusInfo, selectedDevice: string): string {
        if (selectedDevice === "null" || selectedDevice == undefined) {
            if (status === HardwareStatusInfo.NotConnected || status === HardwareStatusInfo.NoDevice) {
                return "fas fa-times";
            }
        }
        return "";
    }

    public hardwareStatusToColor(status: HardwareStatusInfo): string {
        if (status === HardwareStatusInfo.NoDevice || status === HardwareStatusInfo.NotConnected) {
            return "red";
        }
        return "";
    }

    public hardwareStatusToMessage(status: HardwareStatusInfo, selectedDevice: string): string {
        if (selectedDevice == "null" || selectedDevice == undefined) {
            switch (status) {
                case HardwareStatusInfo.Connecting:
                    return "Connecting to hardware";
                case HardwareStatusInfo.Connected:
                    return "Hardware connected";
                default:
                    return "No hardware connected";
            }
        }
        return "CAM connected";
    }

    private clientAndServerStatusToIcon(clientStatus: ClientStatus, serverStatus: ServerStatus): string {
        if (
            clientStatus == ClientStatus.UnsupportedVersion ||
            clientStatus == ClientStatus.NotAvailable ||
            serverStatus == ServerStatus.Unreachable
        )
            return "fas fa-times";
        else if (clientStatus == ClientStatus.OutdatedVersion || serverStatus == ServerStatus.Offline)
            return "fas fa-exclamation-triangle";

        return "";
    }

    private clientAndServerStatusToColor(clientStatus: ClientStatus, serverStatus: ServerStatus): string {
        if (clientStatus == ClientStatus.OutdatedVersion || serverStatus == ServerStatus.Offline) return "carrot";
        else if (
            clientStatus == ClientStatus.NotAvailable ||
            clientStatus == ClientStatus.UnsupportedVersion ||
            serverStatus == ServerStatus.Unreachable
        )
            return "red";

        return "white";
    }

    private clientAndServerStatusToMessage(clientStatus: ClientStatus, serverStatus: ServerStatus): string {
        if (serverStatus == ServerStatus.Unreachable) return "Unable to connect to the Sorama server";
        else if (clientStatus == ClientStatus.NotAvailable) return "Acquisition service is not available";
        else if (clientStatus == ClientStatus.UnsupportedVersion)
            return "Acquisition service has an unsupported version, please update";
        else if (clientStatus == ClientStatus.OutdatedVersion)
            return "Acquisition service has an outdated version, please update";
        else if (serverStatus == ServerStatus.Offline)
            return "Offline, some features might not be available until you connect with the Sorama server";

        return "Services are available";
    }

    public urgencyToClass(urgency: NotificationUrgency): string {
        switch (urgency) {
            case NotificationUrgency.Info:
                return "info";
            case NotificationUrgency.Warning:
                return "warning";
            case NotificationUrgency.Error:
                return "error";
        }
    }

    public urgencyToIcon(urgency: NotificationUrgency): string {
        switch (urgency) {
            case NotificationUrgency.Info:
                return "fas fa-info-circle";
            case NotificationUrgency.Warning:
                return "fas fa-exclamation-triangle";
            case NotificationUrgency.Error:
                return "fas fa-exclamation-triangle";
        }
    }

    private notificationsChanged = (changes: Splice<INotification>[]): void => {
        if (this.shellViewManager.rightSidebarPanel) return;

        let added = false;
        for (const change of changes) {
            for (let i = 0; i < change.addedCount; i++) {
                const notification = this.notificationService.notifications[i + change.index];
                this.newNotifications.push(notification);
                this.removeNotificationAfter(notification, 10000);
                added = true;
                this.unreadNotifications++;
            }

            for (const removed of change.removed) {
                const index = this.newNotifications.findIndex((x) => x === removed);
                if (index !== -1) this.newNotifications.splice(index, 1);
            }
        }

        if (added && this.notificationIconElement) {
            this.notificationIconElement.classList.add("notification-flash");
            window.setTimeout(() => {
                if (this.notificationIconElement.classList.contains("notification-flash"))
                    this.notificationIconElement.classList.remove("notification-flash");
            }, 500);
        }
    };

    private removeNotificationAfter(notification: INotification, timeout: number): void {
        window.setTimeout(() => {
            const index = this.newNotifications.findIndex((x) => x === notification);
            if (index === -1) return;

            this.newNotifications.splice(index, 1);
        }, timeout);
    }

    private navigateToSidebar(module: IWorkflowModuleConfig): void {
        // Clear all newnotifications
        this.newNotifications.splice(0, this.newNotifications.length);
        this.unreadNotifications = 0;

        this.shellViewManager.openRightSideBar(module);
    }

    private closeSidebar(): void {
        this.shellViewManager.closeRightSideBar();
    }

    public toggleUserInfoOrSignIn(): void {
        if (
            this.shellViewManager.rightSidebarPanel &&
            this.shellViewManager.rightSidebarPanel.config.name === UserWorkflowModuleCatalog.UserInformationModule.name
        ) {
            // Already open, close
            this.closeSidebar();
            return;
        }

        // Not open, open login if not signed in, userinformation if signed in
        if (this.authentication.isSignedIn) {
            this.navigateToSidebar(UserWorkflowModuleCatalog.UserInformationModule);
        } else {
            this.authentication
                .signIn()
                .then(() => {
                    this.navigateToSidebar(UserWorkflowModuleCatalog.UserInformationModule);
                })
                .catch((e) => {
                    if (e.cancelled) return;
                    // Ignore error here, there is already a notification for it
                    this.notificationService.registerNotification(
                        "Sign in",
                        e && e.message ? e.message : "Unable to sign in",
                        NotificationUrgency.Error
                    );
                });
        }
    }

    // Needed so that it reacts to changes in the navigation ordering
    private get routerNavigation() {
        return this.router.navigation;
    }
}
